import { endOfDay, format, startOfDay, subDays } from 'date-fns'
import { observer } from 'mobx-react'
import React, { ReactElement, useEffect, useState } from 'react'
import { Container } from '../../components/Animations'
import { InlinePill } from '../../components/DashboardPrimitives'
import LineChart, { ChartPoint } from '../../components/LineChart'
import MoneySparkline from '../Money/MoneySparkline'
import LoadingBar from '../../components/LoadingBar'
import PageHeader from '../../components/PageHeader'
import AnalyticsMetrics from '../../state/Analytics'
import Events, { AssociatedEntityType, ManagedPaymentEvent, RadomEvent } from '../../state/Events'
import Radom, { Customer, ISubscription } from '../../state/Radom'
import Treasury from '../../state/Treasury'
import User from '../../state/User'
import { getMethodLogoByTicker, prodNetworks, testNetworks } from '../../util/Managed'
import { formatCurrency, useTestMode } from '../../util/Util'
import { usePermissions } from '../../util/permissions'
import {
  AnalyticsWorkspace,
  AssetLogo,
  BarFill,
  BarTrack,
  CompositionSegment,
  CompositionTrack,
  LegendDot,
  PanelStatLabel,
  PanelStatRow,
  PanelStatValue,
  StatSpark,
  BreakdownAmount,
  BreakdownFigures,
  BreakdownGrid,
  BreakdownList,
  BreakdownName,
  BreakdownRow,
  BreakdownShare,
  BreakdownSurface,
  BreakdownTopRow,
  ChartHead,
  ChartSurface,
  HeaderPillSlot,
  HeaderRow,
  LoadingRows,
  MutedNote,
  PanelLabel,
  PanelLabelStack,
  PanelMeta,
  Segmented,
  SegmentedButton,
  StatCell,
  StatDelta,
  StatLabel,
  StatStrip,
  StatValue,
  StatValueRow
} from './AnalyticsStyles'

// Same convention as the overview dashboard: numDays is double the visible
// window, the first half is the comparison period and the second half is the
// period on screen.
const TIME_RANGES = [
  { label: '7D', title: 'Last 7 days', numDays: 14 },
  { label: '30D', title: 'Last 30 days', numDays: 60 },
  { label: '90D', title: 'Last 90 days', numDays: 180 },
  { label: '6M', title: 'Last 6 months', numDays: 365 },
  { label: '1Y', title: 'Last year', numDays: 730 }
] as const

const DAY_MS = 24 * 60 * 60 * 1000

// Semantic chart accent, matching the overview dashboard's revenue series.
const VOLUME_ACCENT = '#3464e6'
const NET_ACCENT = '#0e9f6e'
const COUNT_ACCENT = '#7c5cff'

// Categorical palette for breakdown rows; mid-saturation hues that hold on
// both themes. Overflow rows ("Other") stay neutral.
const BREAKDOWN_PALETTE = ['#3464e6', '#0e9f6e', '#e8873a', '#7c5cff', '#d64f7c', '#0f9bb8']
const OTHER_COLOR = 'var(--text-faint, #9aa3b2)'

const rowColor = (label: string, index: number): string =>
  label === 'Other' ? OTHER_COLOR : BREAKDOWN_PALETTE[index % BREAKDOWN_PALETTE.length]

interface DeltaInfo {
  tone: 'up' | 'down' | 'flat'
  label: string
}

interface FunnelTotals {
  curStarted: number
  curCompleted: number
  prevStarted: number
  prevCompleted: number
}

interface BreakdownRowData {
  key: string
  label: string
  amount: number
  count: number
  share: number
  logo?: string
}

interface StatItem {
  key: string
  label: string
  spark?: ChartPoint[]
  sparkLabel?: string
  value?: string
  delta?: DeltaInfo
  hint?: string
}

const sumPoints = (points: ChartPoint[]): number =>
  points.reduce((total, point) => total + point.y, 0)

const currentHalf = (series: ChartPoint[]): ChartPoint[] =>
  series.slice(Math.floor(series.length / 2))

const previousHalf = (series: ChartPoint[]): ChartPoint[] =>
  series.slice(0, Math.floor(series.length / 2))

const getDelta = (current: number, previous: number): DeltaInfo => {
  if (previous <= 0 && current <= 0) {
    return { tone: 'flat', label: '0%' }
  }
  if (previous <= 0) {
    return { tone: 'up', label: 'New' }
  }

  const percentage = ((current - previous) / previous) * 100
  if (!Number.isFinite(percentage) || Math.abs(percentage) < 0.05) {
    return { tone: 'flat', label: '0%' }
  }

  const magnitude = Math.abs(percentage) >= 100 ? percentage.toFixed(0) : percentage.toFixed(1)
  return {
    tone: percentage > 0 ? 'up' : 'down',
    label: `${percentage > 0 ? '+' : ''}${magnitude}%`
  }
}

const isRevenuePaymentEvent = (event: RadomEvent): event is ManagedPaymentEvent =>
  event.name === 'PaymentEvent' ||
  event.name === 'SubscriptionPaymentEvent' ||
  event.name === 'RecurringPaymentEvent'

const getSourceLabel = (entityType?: AssociatedEntityType): string => {
  switch (entityType) {
  case AssociatedEntityType.Invoice:
    return 'Invoices'
  case AssociatedEntityType.PaymentLink:
  case AssociatedEntityType.DonationLink:
    return 'Payment links'
  case AssociatedEntityType.CheckoutSession:
    return 'Checkout sessions'
  case AssociatedEntityType.PaymentSession:
    return 'Payment sessions'
  case AssociatedEntityType.Subscription:
    return 'Subscriptions'
  case AssociatedEntityType.OpenBanking:
    return 'Open banking'
  default:
    return 'Other'
  }
}

const AnalyticsPage = observer((): ReactElement => {
  const { has, isLoaded: permissionsLoaded } = usePermissions()
  const canReadEvents = has('ReadEvents')
  const canReadCustomers = has('ReadCustomers')
  const [testMode] = useTestMode()
  const [topContacts, setTopContacts] = useState<Customer[]>([])
  const [contactsLoading, setContactsLoading] = useState(false)

  // Server-aggregated lifetime spend per contact; not range-scoped, so it
  // loads once per mode rather than on every range switch.
  useEffect(() => {
    if (!permissionsLoaded || !canReadCustomers) {
      setTopContacts([])
      return
    }
    setContactsLoading(true)
    Radom.listCustomers(0, 5, testMode ? testNetworks : prodNetworks, 'totalSpendDescending')
      .then(response => setTopContacts(response.data.filter(c => Number(c.totalSpend || 0) > 0)))
      .catch(err => {
        console.error('Failed to load top contacts', err)
        setTopContacts([])
      })
      .finally(() => setContactsLoading(false))
  }, [permissionsLoaded, canReadCustomers, testMode])

  const [rangeIdx, setRangeIdx] = useState(1)
  const range = TIME_RANGES[rangeIdx]
  const displayDays = range.numDays / 2
  const [chartMode, setChartMode] = useState<'gross' | 'net' | 'count'>('gross')

  const canReadSubscriptions = has('ReadSubscriptions')
  const [subsStats, setSubsStats] = useState<{
    active: number
    created: number
    cancelled: number
    any: boolean
  } | null>(null)
  const [subsLoading, setSubsLoading] = useState(false)

  // Subscription pulse: the active total comes from the status-filtered query
  // so it stays honest under pagination; new and cancelled counts come from
  // the period window (fetched up to 500 rows, plenty for current volumes).
  useEffect(() => {
    if (!permissionsLoaded || !canReadSubscriptions) {
      setSubsStats(null)
      return
    }
    const periodStart = startOfDay(subDays(new Date(), displayDays - 1))
    const networks = testMode ? testNetworks : prodNetworks
    setSubsLoading(true)
    Promise.all([
      Radom.listSubscriptions(
        new Date().toISOString(),
        periodStart.toISOString(),
        periodStart.toISOString(),
        networks,
        ['active', 'cancelled', 'expired'],
        0,
        500
      ),
      Radom.listSubscriptions(
        new Date().toISOString(),
        new Date(0).toISOString(),
        new Date(0).toISOString(),
        networks,
        ['active'],
        0,
        1
      )
    ])
      .then(([windowed, activeOnly]) => {
        const created = windowed.data.filter(sub =>
          new Date(sub.createdAt).getTime() >= periodStart.getTime()).length
        const cancelled = windowed.data.filter((sub: ISubscription) =>
          sub.cancelledAt && new Date(sub.cancelledAt).getTime() >= periodStart.getTime()).length
        setSubsStats({
          active: activeOnly.total,
          created,
          cancelled,
          any: activeOnly.total > 0 || windowed.total > 0
        })
      })
      .catch(err => {
        console.error('Failed to load subscription pulse', err)
        setSubsStats(null)
      })
      .finally(() => setSubsLoading(false))
  }, [permissionsLoaded, canReadSubscriptions, testMode, rangeIdx])

  const [grossSeries, setGrossSeries] = useState<ChartPoint[]>([])
  const [netSeries, setNetSeries] = useState<ChartPoint[]>([])
  const [paymentSeries, setPaymentSeries] = useState<ChartPoint[]>([])
  const [funnel, setFunnel] = useState<FunnelTotals | null>(null)
  const [sourceRows, setSourceRows] = useState<BreakdownRowData[]>([])
  const [assetRows, setAssetRows] = useState<BreakdownRowData[]>([])
  const [isMetricsLoading, setIsMetricsLoading] = useState(true)

  const defaultCurrency = User.organization?.defaultCurrency ?? 'USD'
  const formatMoney = (value: number): string => formatCurrency(value, defaultCurrency)
  const formatAxisMoney = (value: number): string => {
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: defaultCurrency,
        notation: 'compact',
        maximumFractionDigits: 1
      }).format(value)
    } catch {
      return `${new Intl.NumberFormat('en-US', {
        notation: 'compact',
        maximumFractionDigits: 1
      }).format(value)} ${defaultCurrency}`
    }
  }

  // Same conversion approach the overview metrics use: default currency is 1:1,
  // then treasury price quotes, then the conversion rates carried on the event.
  const getConversionRate = (from: string, event?: ManagedPaymentEvent): number | undefined => {
    if (from.toLowerCase() === defaultCurrency.toLowerCase()) {
      return 1
    }

    const quote = Treasury.priceQuotes.find(priceQuote =>
      priceQuote.from.toLowerCase() === from.toLowerCase())
    if (quote) {
      return quote.value
    }

    const eventRate = event?.conversionRates?.find(rate =>
      rate.from.toLowerCase() === from.toLowerCase() &&
      rate.to.toLowerCase() === defaultCurrency.toLowerCase())
    return eventRate ? Number(eventRate.rate) : undefined
  }

  const getPaymentValue = (event: ManagedPaymentEvent): number => {
    const summary = event.paymentSummary
    if (summary?.grossAmount && summary.currency) {
      const summaryRate = getConversionRate(summary.currency, event)
      if (summaryRate !== undefined) {
        return Number(summary.grossAmount) * summaryRate
      }
    }

    return Number(event.amount) * (getConversionRate(event.paymentMethod.ticker, event) ?? 1)
  }

  const computeBreakdowns = (): { sources: BreakdownRowData[], assets: BreakdownRowData[] } => {
    const curStartMs = Date.now() - displayDays * DAY_MS
    const sourceTotals = new Map<string, { amount: number, count: number }>()
    const assetTotals = new Map<string, { amount: number, count: number }>()

    Events.managedEvents.forEach(event => {
      if (!isRevenuePaymentEvent(event)) {
        return
      }
      if ((event.paymentMethod.isTestnet && !testMode) || (!event.paymentMethod.isTestnet && testMode)) {
        return
      }

      const eventTime = new Date(event.date).getTime()
      if (eventTime < curStartMs) {
        return
      }

      const amount = getPaymentValue(event)
      const sourceLabel = getSourceLabel(event.associatedEntity?.entityType)
      const sourceEntry = sourceTotals.get(sourceLabel) ?? { amount: 0, count: 0 }
      sourceEntry.amount += amount
      sourceEntry.count += 1
      sourceTotals.set(sourceLabel, sourceEntry)

      const ticker = event.paymentMethod.ticker || 'Unknown'
      const assetEntry = assetTotals.get(ticker) ?? { amount: 0, count: 0 }
      assetEntry.amount += amount
      assetEntry.count += 1
      assetTotals.set(ticker, assetEntry)
    })

    const toRows = (
      totals: Map<string, { amount: number, count: number }>,
      maxRows: number,
      withLogos: boolean
    ): BreakdownRowData[] => {
      const entries = [...totals.entries()]
        .map(([label, entry]) => ({ label, ...entry }))
        .sort((a, b) => b.amount - a.amount)
      const grandTotal = entries.reduce((total, entry) => total + entry.amount, 0)

      const kept = entries.slice(0, maxRows)
      const rest = entries.slice(maxRows)
      if (rest.length > 0) {
        kept.push({
          label: 'Other',
          amount: rest.reduce((total, entry) => total + entry.amount, 0),
          count: rest.reduce((total, entry) => total + entry.count, 0)
        })
      }

      return kept.map(entry => ({
        key: entry.label,
        label: entry.label,
        amount: entry.amount,
        count: entry.count,
        share: grandTotal > 0 ? entry.amount / grandTotal : 0,
        logo: withLogos && entry.label !== 'Other'
          ? getMethodLogoByTicker(entry.label) as string | undefined
          : undefined
      }))
    }

    return {
      sources: toRows(sourceTotals, 6, false),
      assets: toRows(assetTotals, 5, true)
    }
  }

  useEffect(() => {
    if (!User.organization || !canReadEvents) {
      return
    }

    let cancelled = false

    const load = async (): Promise<void> => {
      setIsMetricsLoading(true)

      await Events.loadEvents(testMode, range.numDays)

      try {
        await User.getOrganization()
        await Treasury.loadPriceQuotes()
      } catch {}

      const [gross, net, oneTime, recurring] = await Promise.all([
        AnalyticsMetrics.getGrossVolume(range.numDays, testMode),
        AnalyticsMetrics.getNetVolume(range.numDays, testMode),
        AnalyticsMetrics.getOneTimePayments(range.numDays, testMode),
        AnalyticsMetrics.getRecurringPayments(range.numDays, testMode)
      ])

      let funnelTotals: FunnelTotals | null = null
      try {
        // The summary endpoint caps a query at 366 days, so the comparison
        // window (double the visible period) is fetched as two windows and
        // stays legal at every range including 1Y.
        const boundaryDate = startOfDay(subDays(new Date(), displayDays - 1))
        const [currentRows, previousRows] = await Promise.all([
          Radom.getCheckoutFunnelSummary(boundaryDate.toISOString(), endOfDay(new Date()).toISOString()),
          Radom.getCheckoutFunnelSummary(
            startOfDay(subDays(new Date(), range.numDays - 1)).toISOString(),
            endOfDay(subDays(new Date(), displayDays)).toISOString()
          )
        ])
        funnelTotals = { curStarted: 0, curCompleted: 0, prevStarted: 0, prevCompleted: 0 }
        for (const row of currentRows) {
          funnelTotals.curStarted += row.startedSessions
          funnelTotals.curCompleted += row.completedSessions
        }
        for (const row of previousRows) {
          funnelTotals.prevStarted += row.startedSessions
          funnelTotals.prevCompleted += row.completedSessions
        }
      } catch (err) {
        console.error('Failed to load checkout funnel summary', err)
      }

      if (cancelled) {
        return
      }

      const breakdowns = computeBreakdowns()
      setGrossSeries(gross)
      setNetSeries(net)
      setPaymentSeries(oneTime.map((point, idx) => ({
        x: point.x,
        y: point.y + (recurring[idx]?.y ?? 0)
      })))
      setFunnel(funnelTotals)
      setSourceRows(breakdowns.sources)
      setAssetRows(breakdowns.assets)
      setIsMetricsLoading(false)
    }

    void load()

    return () => {
      cancelled = true
    }
  }, [testMode, rangeIdx, User.organization, canReadEvents])

  const isLoading = isMetricsLoading || Events.isLoading

  const grossCurTotal = sumPoints(currentHalf(grossSeries))
  const grossPrevTotal = sumPoints(previousHalf(grossSeries))
  const netCurTotal = sumPoints(currentHalf(netSeries))
  const netPrevTotal = sumPoints(previousHalf(netSeries))
  const paymentsCurTotal = Math.round(sumPoints(currentHalf(paymentSeries)))
  const paymentsPrevTotal = Math.round(sumPoints(previousHalf(paymentSeries)))
  const averageCur = paymentsCurTotal > 0 ? grossCurTotal / paymentsCurTotal : 0
  const averagePrev = paymentsPrevTotal > 0 ? grossPrevTotal / paymentsPrevTotal : 0

  const stats: StatItem[] = [
    {
      key: 'volume',
      label: 'Payment volume',
      spark: isLoading ? undefined : currentHalf(grossSeries),
      sparkLabel: `Daily payment volume, ${range.title.toLowerCase()}`,
      value: isLoading ? undefined : formatMoney(grossCurTotal),
      delta: isLoading ? undefined : getDelta(grossCurTotal, grossPrevTotal),
      hint: `Previous period ${formatMoney(grossPrevTotal)}`
    },
    {
      key: 'net',
      label: 'Net volume',
      value: isLoading ? undefined : formatMoney(netCurTotal),
      delta: isLoading ? undefined : getDelta(netCurTotal, netPrevTotal),
      hint: `Previous period ${formatMoney(netPrevTotal)}`
    },
    {
      key: 'payments',
      label: 'Payments',
      spark: isLoading ? undefined : currentHalf(paymentSeries),
      sparkLabel: `Daily payment count, ${range.title.toLowerCase()}`,
      value: isLoading ? undefined : paymentsCurTotal.toLocaleString(),
      delta: isLoading ? undefined : getDelta(paymentsCurTotal, paymentsPrevTotal),
      hint: `Previous period ${paymentsPrevTotal.toLocaleString()}`
    },
    {
      key: 'average',
      label: 'Average payment',
      value: isLoading ? undefined : formatMoney(averageCur),
      delta: isLoading || paymentsCurTotal === 0 || paymentsPrevTotal === 0
        ? undefined
        : getDelta(averageCur, averagePrev),
      hint: `Previous period ${formatMoney(averagePrev)}`
    }
  ]

  if (!isLoading && funnel && funnel.curStarted > 0) {
    const curRate = (funnel.curCompleted / funnel.curStarted) * 100
    const prevRate = funnel.prevStarted > 0
      ? (funnel.prevCompleted / funnel.prevStarted) * 100
      : undefined
    const pointsDelta = prevRate !== undefined ? curRate - prevRate : undefined

    stats.push({
      key: 'conversion',
      label: 'Checkout conversion',
      value: `${curRate.toFixed(1)}%`,
      delta: pointsDelta !== undefined
        ? {
          tone: pointsDelta > 0.05 ? 'up' : pointsDelta < -0.05 ? 'down' : 'flat',
          label: `${pointsDelta > 0 ? '+' : ''}${pointsDelta.toFixed(1)} pts`
        }
        : undefined,
      hint: `${funnel.curCompleted.toLocaleString()} completed of ${funnel.curStarted.toLocaleString()} started sessions`
    })
  } else if (isLoading) {
    stats.push({ key: 'conversion', label: 'Checkout conversion' })
  }

  const contactSpendTotal = topContacts.reduce((sum, contact) => sum + Number(contact.totalSpend || 0), 0)
  const contactRows: BreakdownRowData[] = topContacts.map(contact => {
    const amount = Number(contact.totalSpend || 0)
    return {
      key: contact.id,
      label: contact.name?.trim() || contact.companyName?.trim() || contact.email?.trim() || contact.id.slice(0, 8),
      amount,
      share: contactSpendTotal > 0 ? amount / contactSpendTotal : 0,
      count: 0
    }
  })

  const chartConfig = {
    gross: {
      label: 'Volume',
      meta: `Gross payment volume in ${defaultCurrency}`,
      series: grossSeries,
      accent: VOLUME_ACCENT,
      formatValue: formatMoney,
      formatAxis: formatAxisMoney
    },
    net: {
      label: 'Net',
      meta: `Net payment volume in ${defaultCurrency}, after fees`,
      series: netSeries,
      accent: NET_ACCENT,
      formatValue: formatMoney,
      formatAxis: formatAxisMoney
    },
    count: {
      label: 'Payments',
      meta: 'Completed payments per day',
      series: paymentSeries,
      accent: COUNT_ACCENT,
      formatValue: (value: number): string => Math.round(value).toLocaleString(),
      formatAxis: (value: number): string => Math.round(value).toLocaleString()
    }
  }[chartMode]

  const chartData = currentHalf(chartConfig.series)
  const hasAnyVolume = grossCurTotal > 0 || grossPrevTotal > 0 || paymentsCurTotal > 0
  const xAxisFormat = (value: number): string =>
    format(new Date(value), range.numDays > 90 ? 'MMM yyyy' : 'MMM d')

  const renderBreakdownPanel = (
    title: string,
    rows: BreakdownRowData[],
    emptyNote: string,
    meta?: string,
    loading?: boolean
  ): ReactElement => <BreakdownSurface>
    <PanelLabelStack>
      <PanelLabel>{title}</PanelLabel>
      <PanelMeta>{meta ?? range.title}</PanelMeta>
    </PanelLabelStack>
    {(loading ?? isLoading)
      ? <LoadingRows>
        <LoadingBar style={{ height: 11, width: '86%' }} />
        <LoadingBar style={{ height: 11, width: '64%' }} />
        <LoadingBar style={{ height: 11, width: '48%' }} />
      </LoadingRows>
      : rows.length === 0
        ? <MutedNote>{emptyNote}</MutedNote>
        : <BreakdownList>
          {rows.length > 1 && <CompositionTrack aria-hidden='true'>
            {rows.map((row, index) => <CompositionSegment
              key={row.key}
              $color={rowColor(row.label, index)}
              $width={row.share * 100} />)}
          </CompositionTrack>}
          {rows.map((row, index) => {
            const sharePercent = Math.round(row.share * 100)
            const shareLabel = sharePercent === 0 && row.amount > 0 ? '<1%' : `${sharePercent}%`
            return <BreakdownRow
              key={row.key}
              title={row.count > 0
                ? `${row.count.toLocaleString()} ${row.count === 1 ? 'payment' : 'payments'}`
                : undefined}
            >
              <BreakdownTopRow>
                <BreakdownName>
                  {row.logo
                    ? <AssetLogo src={row.logo} alt='' />
                    : <LegendDot $color={rowColor(row.label, index)} aria-hidden='true' />}
                  {row.label}
                </BreakdownName>
                <BreakdownFigures>
                  <BreakdownAmount>{formatMoney(row.amount)}</BreakdownAmount>
                  <BreakdownShare>{shareLabel}</BreakdownShare>
                </BreakdownFigures>
              </BreakdownTopRow>
              <BarTrack>
                <BarFill
                  $color={rowColor(row.label, index)}
                  style={{ width: `${Math.max(row.share * 100, row.amount > 0 ? 1.5 : 0)}%` }} />
              </BarTrack>
            </BreakdownRow>
          })}
        </BreakdownList>}
  </BreakdownSurface>

  if (permissionsLoaded && !canReadEvents) {
    return <Container style={{ flexDirection: 'column' }}>
      <AnalyticsWorkspace>
        <HeaderRow>
          <PageHeader title='Analytics' subTitle='Payment performance across your account.' />
        </HeaderRow>
        <BreakdownSurface>
          <MutedNote>
            Your role does not have permission to view analytics.
            Ask an organization admin to grant access to events.
          </MutedNote>
        </BreakdownSurface>
      </AnalyticsWorkspace>
    </Container>
  }

  return <Container style={{ flexDirection: 'column' }}>
    <AnalyticsWorkspace>
      <HeaderRow>
        <PageHeader
          title='Analytics'
          subTitle={`${range.title} compared with the prior period.`}
        />
        {testMode && <HeaderPillSlot>
          <InlinePill $tone='amber'>Test mode</InlinePill>
        </HeaderPillSlot>}
      </HeaderRow>

      <StatStrip>
        {stats.map(stat => <StatCell key={stat.key} title={stat.value !== undefined ? stat.hint : undefined}>
          <StatLabel>{stat.label}</StatLabel>
          <StatValueRow>
            {stat.value === undefined
              ? <LoadingBar style={{ height: 21, width: 84 }} />
              : <>
                <StatValue>{stat.value}</StatValue>
                {stat.delta && <StatDelta $tone={stat.delta.tone}>{stat.delta.label}</StatDelta>}
              </>}
          </StatValueRow>
          {stat.spark && stat.spark.some(point => point.y > 0) &&
            <StatSpark>
              <MoneySparkline
                points={stat.spark}
                height={26}
                ariaLabel={stat.sparkLabel ?? stat.label} />
            </StatSpark>}
        </StatCell>)}
      </StatStrip>

      <ChartSurface>
        <ChartHead>
          <div style={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 14 }}>
            <PanelLabelStack>
              <PanelLabel>Payments over time</PanelLabel>
              <PanelMeta>{chartConfig.meta}</PanelMeta>
            </PanelLabelStack>
            <Segmented aria-label='Chart series'>
              {(['gross', 'net', 'count'] as const).map(mode => <SegmentedButton
                key={mode}
                $active={chartMode === mode}
                type='button'
                onClick={() => setChartMode(mode)}
              >
                {mode === 'gross' ? 'Volume' : mode === 'net' ? 'Net' : 'Payments'}
              </SegmentedButton>)}
            </Segmented>
          </div>
          <Segmented aria-label='Time range'>
            {TIME_RANGES.map((option, idx) => <SegmentedButton
              key={option.label}
              $active={idx === rangeIdx}
              type='button'
              title={option.title}
              onClick={() => setRangeIdx(idx)}
            >
              {option.label}
            </SegmentedButton>)}
          </Segmented>
        </ChartHead>
        {!isLoading && !hasAnyVolume &&
          <MutedNote>No payments recorded in this period yet.</MutedNote>}
        <LineChart
          data={chartData}
          height={300}
          accentColor={chartConfig.accent}
          isLoading={isLoading}
          showYAxis
          minTickGap={90}
          formatXAxis={xAxisFormat}
          formatTooltipX={value => format(new Date(value), 'MMM d, yyyy')}
          formatTooltipY={chartConfig.formatValue}
          formatYAxis={chartConfig.formatAxis}
        />
      </ChartSurface>

      <BreakdownGrid>
        {renderBreakdownPanel(
          'Volume by source',
          sourceRows,
          'No completed payments in this period yet.'
        )}
        {renderBreakdownPanel(
          'Volume by asset',
          assetRows,
          'No completed payments in this period yet.'
        )}
        {canReadCustomers && renderBreakdownPanel(
          'Top contacts',
          contactRows,
          'No contact revenue recorded yet.',
          'All time',
          contactsLoading
        )}
        {canReadSubscriptions && (subsLoading || subsStats?.any) && <BreakdownSurface>
          <PanelLabelStack>
            <PanelLabel>Subscriptions</PanelLabel>
            <PanelMeta>{range.title}</PanelMeta>
          </PanelLabelStack>
          {subsLoading || !subsStats
            ? <LoadingRows>
              <LoadingBar style={{ height: 11, width: '70%' }} />
              <LoadingBar style={{ height: 11, width: '52%' }} />
            </LoadingRows>
            : <div>
              <PanelStatRow>
                <PanelStatLabel>Active subscribers</PanelStatLabel>
                <PanelStatValue>{subsStats.active.toLocaleString()}</PanelStatValue>
              </PanelStatRow>
              <PanelStatRow>
                <PanelStatLabel>New subscriptions</PanelStatLabel>
                <PanelStatValue $tone={subsStats.created > 0 ? 'green' : undefined}>
                  {subsStats.created > 0 ? `+${subsStats.created.toLocaleString()}` : '0'}
                </PanelStatValue>
              </PanelStatRow>
              <PanelStatRow>
                <PanelStatLabel>Cancelled</PanelStatLabel>
                <PanelStatValue $tone={subsStats.cancelled > 0 ? 'red' : undefined}>
                  {subsStats.cancelled.toLocaleString()}
                </PanelStatValue>
              </PanelStatRow>
            </div>}
        </BreakdownSurface>}
      </BreakdownGrid>
    </AnalyticsWorkspace>
  </Container>
})

export default AnalyticsPage
